/** @format */
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    EventEmitter,
    Inject,
    input,
    Optional,
    Output,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { Attainment, CourseUnit, Module, OtmId } from 'common-typescript/types';
import { PLAN_ACTIONS_SERVICE_INJECTION_TOKEN, PlanActionsService } from '../../plan/plan-actions-service/plan-actions.service';
import { CourseUnitStateObject } from '../../service/plan-state.service';
import { ScrollService } from '../../service/scroll.service';
import { UniversityService } from '../../service/university.service';
import { Action, InputType } from '../../study-boxes/study-action-box/study-action-box.component';
import { PlanStructureContext } from '../plan-structure-context';

@Component({
    selector: 'sis-plan-structure-implicit-course-unit',
    templateUrl: './plan-structure-implicit-course-unit.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class PlanStructureImplicitCourseUnitComponent {
    readonly courseUnit = input.required<CourseUnit>();
    readonly headingLevel = input.required<number>();
    readonly showSkipLink = input<boolean>(false);
    readonly selected = input<boolean>();
    readonly action = input<Action>();
    readonly inputType = input<InputType>();
    readonly actionInputId = input<string>();
    readonly actionInputName = input<string>();
    @Output() actionChange? = new EventEmitter<OtmId>();

    private readonly courseUnitStateObject = computed<CourseUnitStateObject | undefined>(
        () => this.planStructureContext.planStateObject().courseUnits?.[this.courseUnit().id],
    );

    protected readonly attainment = computed<Attainment | undefined>(() => {
        const courseUnitStateObject = this.courseUnitStateObject();
        return courseUnitStateObject ? this.planStructureContext.planData().attainmentsById[courseUnitStateObject.attainmentId] : undefined;
    });

    protected readonly isSubstituted = computed(() => this.attainment()?.state === 'SUBSTITUTED');
    protected readonly newerVersionAvailable = computed(() => this.courseUnitStateObject()?.newerVersionAvailable ?? false);

    private readonly parentModuleId = computed<OtmId | undefined>(() => this.courseUnitStateObject()?.selectedParentModuleId);
    protected readonly parentModule = computed<Module | undefined>(
        () => this.planStructureContext.planData()?.modulesById[this.parentModuleId()],
    );
    private readonly parentModuleElementId = computed<string | undefined>(
        () => this.planStructureContext.planStateObject().modules?.[this.parentModuleId()]?.elementId,
    );

    protected readonly isCrossStudyCourseUnit = computed(() => {
        const courseUnit = this.courseUnit();
        const isCourseUnitsOrganisationSameAsStudents = courseUnit.universityOrgIds?.includes(
            this.universityService.getCurrentUniversityOrgId(),
        );
        return !isCourseUnitsOrganisationSameAsStudents && courseUnit.cooperationNetworkDetails?.networks.length > 0;
    });

    protected readonly studyState = computed<'DRAFT' | 'DELETED' | undefined>(() => {
        const { documentState } = this.courseUnit();
        return documentState !== 'ACTIVE' ? documentState : undefined;
    });

    private readonly planStructureContext = inject(PlanStructureContext);

    constructor(
        @Optional() @Inject(PLAN_ACTIONS_SERVICE_INJECTION_TOKEN) private planActionsService: PlanActionsService,
        private universityService: UniversityService,
        private scrollService: ScrollService,
    ) {}

    openCourseUnitInfoModal() {
        this.planActionsService?.openCourseUnitInfoModal(this.courseUnit());
    }

    scrollToParentModule() {
        this.scrollService.scrollToElementById(this.parentModuleElementId());
    }
}
